import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, ProgressLine, Tag, attTone } from "@/components/kit";
import { LinesChart, GOLD, INK } from "@/components/Charts";
import { SalesEntryForm } from "@/components/Forms";
import { companySales, monthSales, teamOf, nameMap, pct } from "@/lib/selectors";
import { PROFILES, MONTH_LABEL, TODAY } from "@/data/mock";
import { brl, fmtDate } from "@/lib/format";

const cumulative = (entries, goal) => {
  const days = Number(TODAY.slice(8));
  let acc = 0;
  return Array.from({ length: days }, (_, i) => {
    const d = String(i + 1).padStart(2, "0");
    acc += entries.filter((e) => e.date.slice(8) === d).reduce((a, e) => a + Number(e.value), 0);
    return { dia: d, realizado: acc, ritmo: Math.round((goal / 31) * (i + 1)) };
  });
};

export default function OwnerSales() {
  const { state, addSale } = useStore();
  const cid = PROFILES.empresaria.companyId;
  const company = state.companies.find((c) => c.id === cid);
  const total = companySales(state, cid);
  const att = pct(total, company.salesGoal);
  const entries = [...monthSales(state, cid)].sort((a, b) => b.date.localeCompare(a.date));
  const names = nameMap(state);

  return (
    <div data-testid="owner-sales-page">
      <PageHeader eyebrow={MONTH_LABEL} title="Vendas e metas" subtitle="Lance manualmente o total de vendas registrado no seu sistema. Nenhum dado de cliente é necessário." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Panel title="Meta do mês" testId="sales-goal-panel" className="lg:col-span-2">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p data-testid="sales-total-value" className="font-display text-4xl font-semibold">{brl(total)}</p>
              <p className="mt-1 text-sm text-neutral-500">de {brl(company.salesGoal)}</p>
            </div>
            <Tag tone={attTone(att)} testId="sales-att-tag">{att}% atingido</Tag>
          </div>
          <div className="mt-5"><ProgressLine value={att} /></div>
          <div className="mt-8"><LinesChart data={cumulative(entries, company.salesGoal)} xKey="dia" series={[{ key: "realizado", name: "Realizado", color: INK }, { key: "ritmo", name: "Ritmo da meta", color: GOLD, dashed: true }]} height={220} /></div>
        </Panel>
        <Panel title="Lançar venda" subtitle="Total do dia ou por colaboradora" testId="sales-entry-panel">
          <SalesEntryForm companyId={cid} people={teamOf(state, cid)} onSubmit={addSale} />
        </Panel>
      </div>
      <Panel title="Lançamentos do mês" className="mt-6" testId="sales-entries-table">
        <div className="divide-y divide-neutral-100">
          {entries.slice(0, 15).map((e) => (
            <div key={e.id} data-testid={`sales-entry-${e.id}`} className="grid grid-cols-[70px_1fr_auto] items-center gap-4 py-3.5 text-sm">
              <span className="text-xs text-neutral-500">{fmtDate(e.date)}</span>
              <span className="truncate"><span className="font-medium">{e.collaboratorId ? names[e.collaboratorId] : "Venda geral"}</span><span className="text-neutral-400"> · {e.note}</span></span>
              <span className="font-semibold">{brl(e.value)}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
